import React, { useEffect, useState } from 'react';
import axios from 'axios';

export default function DebtDisbursementLinkModal({ row, onClose, onLinked }) {
  const [debts, setDebts] = useState([]);
  const [selectedDebt, setSelectedDebt] = useState(null); // { id, type, label }
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [debtsLoading, setDebtsLoading] = useState(false);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDebts();
  }, []);

  const fetchDebts = async () => {
    setDebtsLoading(true);
    try {
      const res = await axios.get(`/api/balance_sheet/assets/cash_accounts/transactions/${row.id}/debts-for-payment/`);
      const data = res.data;
      setDebts(data.debts || []);
      
      // If already linked, pre-select
      if (row.linked_debt_id) {
        const d = (data.debts || []).find(item => item.id === row.linked_debt_id);
        if (d) {
          setSelectedDebt(d);
          fetchCandidates(d.type, d.id);
        }
      }
    } catch (e) {
      console.error("Failed to fetch debts", e);
    } finally {
      setDebtsLoading(false);
    }
  };

  const fetchCandidates = async (type, id) => {
    setCandidatesLoading(true);
    setCandidates([]);
    setSelectedCandidateId('');
    try {
      const res = await axios.get(`/api/balance_sheet/assets/cash_accounts/transactions/${row.id}/candidate-debt-disbursements/?debt_type=${type}&debt_id=${id}`);
      const data = res.data;
      setCandidates(data.candidates || []);
      
      if (row.linked_debt_txn_id) {
        setSelectedCandidateId(row.linked_debt_txn_id);
      } else if (data.candidates && data.candidates.length === 1) {
        setSelectedCandidateId(data.candidates[0].id);
      }
    } catch (e) {
      console.error("Failed to fetch candidates", e);
    } finally {
      setCandidatesLoading(false);
    }
  };

  const handleDebtChange = (e) => {
    const val = e.target.value;
    if (!val) {
      setSelectedDebt(null);
      setCandidates([]);
      return;
    }
    const [type, id] = val.split(':');
    const d = debts.find(item => String(item.id) === id && item.type === type);
    setSelectedDebt(d);
    fetchCandidates(type, id);
  };

  const handleSave = async () => {
    if (!selectedDebt || !selectedCandidateId) return;
    setLoading(true);
    setError('');
    try {
      await axios.post(`/api/balance_sheet/assets/cash_accounts/transactions/${row.id}/link-debt-disbursement/`, {
        debt_type: selectedDebt.type,
        debt_id: selectedDebt.id,
        candidate_id: selectedCandidateId
      });
      onLinked();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to link disbursement");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:3000 }}>
      <div style={{ width:'550px', background:'#0b1220', border:'1px solid #374151', borderRadius:8, color:'#e5e7eb', padding:20 }}>
        <div style={{ fontSize:18, fontWeight:700, marginBottom:16, borderBottom:'1px solid #374151', paddingBottom:10 }}>
          Link Debt Disbursement
        </div>
        
        {error && <div className="alert alert-danger py-2">{error}</div>}

        <div className="mb-3">
          <label className="form-label text-info">1. Select Debt Account</label>
          <select 
            className="form-select bg-dark text-white border-secondary"
            value={selectedDebt ? `${selectedDebt.type}:${selectedDebt.id}` : ''}
            onChange={handleDebtChange}
            disabled={debtsLoading}
          >
            <option value="">-- Choose Debt --</option>
            {debts.map(d => (
              <option key={`${d.type}:${d.id}`} value={`${d.type}:${d.id}`}>{d.label}</option>
            ))}
          </select>
        </div>

        {selectedDebt && (
          <div className="mb-3">
            <label className="form-label text-info">2. Select Disbursement Transaction on Debt</label>
            {candidatesLoading ? (
              <div className="text-muted small">Searching for matching disbursements...</div>
            ) : candidates.length > 0 ? (
              <div className="list-group">
                {candidates.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    className={`list-group-item list-group-item-action bg-dark text-white border-secondary ${String(selectedCandidateId) === String(c.id) ? 'active' : ''}`}
                    onClick={() => setSelectedCandidateId(c.id)}
                  >
                    <div className="d-flex justify-content-between">
                      <span>{c.description}</span>
                      <span className="fw-bold text-success">${c.amount.toLocaleString()}</span>
                    </div>
                    <div className="small text-muted">Date: {c.post_date || c.transaction_date} | ID: {c.id}</div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="alert alert-warning py-2 small">
                No matching transactions (positive amount / same date) found on this debt account.
              </div>
            )}
          </div>
        )}

        <div className="d-flex justify-content-end gap-2 mt-4">
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>Cancel</button>
          <button 
            className="btn btn-primary" 
            onClick={handleSave} 
            disabled={loading || !selectedDebt || !selectedCandidateId}
          >
            {loading ? 'Linking...' : 'Link Disbursement'}
          </button>
        </div>
      </div>
    </div>
  );
}
